import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import io from 'socket.io-client';

// ============================================================================
// 🎨 THEME & DESIGN SYSTEM (V6.0 Supreme)
// ============================================================================
const THEME = {
  primary: '#4F46E5',      
  primaryHover: '#4338CA', 
  primaryGlow: 'rgba(79, 70, 229, 0.15)',
  secondary: '#0EA5E9',    
  bgBase: '#F8FAFC',       
  bgSurface: '#FFFFFF',    
  bgChat: '#F1F5F9',       
  textMain: '#0F172A',     
  textMuted: '#64748B',    
  border: '#E2E8F0',       
  success: '#10B981',      
  successBg: '#D1FAE5',    
  danger: '#EF4444',       
  dangerBg: '#FEE2E2',     
  warning: '#F59E0B',      
  warningBg: '#FEF3C7',    
  
  selfBubble: 'linear-gradient(135deg, #4F46E5, #6366F1)',
  otherBubble: '#FFFFFF',
  botBubble: '#ECFDF5',
  
  shadowSm: '0 2px 4px rgba(0, 0, 0, 0.02)',
  shadowMd: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.025)',
  shadowLg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

const API_URL = 'http://192.168.1.80:3333';
const SOCKET_OPTS = { reconnection: true, reconnectionAttempts: 10, transports: ['websocket'] };

const OS_STATUSES = ['Pedido da lente', 'Pedido realizado', 'No laboratório', 'Pronto na loja', 'Entregue'];
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// ============================================================================
// 💉 CSS INJECTION
// ============================================================================
const injectStyles = () => {
  if (document.getElementById('supreme-styles')) return;
  const style = document.createElement('style');
  style.id = 'supreme-styles';
  style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    
    * { box-sizing: border-box; font-family: 'Inter', sans-serif; outline: none; }
    
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; }
    ::-webkit-scrollbar-thumb:hover { background: #94A3B8; }

    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes popIn { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
    @keyframes pulseSoft { 0% { box-shadow: 0 0 0 0 ${THEME.primaryGlow}; } 70% { box-shadow: 0 0 0 15px rgba(79,70,229,0); } 100% { box-shadow: 0 0 0 0 rgba(79,70,229,0); } }

    .glass-header { background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border-bottom: 1px solid ${THEME.border}; }
    .glass-input { background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(10px); }
    
    .client-card { transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1); border: 1px solid transparent; }
    .client-card:hover:not(.active) { background: #fff; border-color: ${THEME.border}; box-shadow: ${THEME.shadowSm}; transform: translateX(2px); }
    .client-card.active { background: #fff; border-color: ${THEME.primary}; box-shadow: 0 10px 20px -5px ${THEME.primaryGlow}; position: relative; }
    .client-card.active::before { content: ''; position: absolute; left: -1px; top: 10px; bottom: 10px; width: 4px; background: ${THEME.primary}; border-radius: 0 4px 4px 0; }

    .mac-tabs { background: ${THEME.bgChat}; padding: 4px; border-radius: 12px; display: flex; box-shadow: inset 0 2px 4px rgba(0,0,0,0.02); }
    .mac-tab { padding: 8px 16px; border: none; background: transparent; color: ${THEME.textMuted}; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.3s; position: relative; }
    .mac-tab.active { background: #fff; color: ${THEME.primary}; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }

    .bubble { position: relative; max-width: 70%; padding: 12px 18px; font-size: 14px; line-height: 1.5; border-radius: 20px; box-shadow: 0 2px 5px rgba(0,0,0,0.04); animation: slideUp 0.3s ease-out; }
    .bubble-me { background: ${THEME.selfBubble}; color: #fff; border-bottom-right-radius: 4px; margin-left: auto; box-shadow: 0 4px 10px ${THEME.primaryGlow}; }
    .bubble-other { background: ${THEME.otherBubble}; color: ${THEME.textMain}; border-bottom-left-radius: 4px; margin-right: auto; border: 1px solid ${THEME.border}; }
    .bubble-bot { background: ${THEME.botBubble}; color: #065F46; border-bottom-left-radius: 4px; margin-right: auto; border: 1px solid #A7F3D0; box-shadow: 0 4px 10px rgba(16, 185, 129, 0.1); }

    .input-focus-ring:focus-within { border-color: ${THEME.primary} !important; box-shadow: 0 0 0 3px ${THEME.primaryGlow}; }
    .btn-epic { transition: all 0.2s; cursor: pointer; border: none; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; }
    .btn-epic:active { transform: scale(0.96); }
    .btn-epic:hover { transform: translateY(-2px); box-shadow: ${THEME.shadowMd}; }

    .chat-bg { background-color: #F8FAFC; background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23cbd5e1' fill-opacity='0.2' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E"); }
  `;
  document.head.appendChild(style);
};

// ============================================================================
// 🛠️ HELPERS & UI COMPONENTS
// ============================================================================
const formatDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

const formatTime = (d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const groupMessagesByDate = (messages) => {
  if (!messages || !Array.isArray(messages)) return {};
  const groups = {};
  messages.forEach(msg => {
    const dateKey = new Date(msg.rawDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
  });
  return groups;
};

// Traduz o marco (String) para a quantidade de dias matemáticos
const converterMarcoParaDias = (marco) => {
    if (marco === '0') return 0;
    if (marco === '7d') return 7;
    return parseInt(marco) * 30; // 1 = 30 dias, 2 = 60 dias...
};

// Transforma a tag {{nome}} no texto real e coloca os asteriscos se quiser
const formatarTexto = (textoBase, nomeCompleto) => {
    if (!textoBase) return '';
    const primeiroNome = nomeCompleto ? nomeCompleto.split(' ')[0] : 'Cliente';
    // Substitui {{nome}} (ignorando maiúsculas/minúsculas)
    return textoBase.replace(/{{nome}}/gi, `*${primeiroNome}*`);
};

const Icons = {
  Send: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>,
  Search: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
  Robot: ({active}) => <svg width="24" height="24" viewBox="0 0 24 24" fill={active ? THEME.success : "none"} stroke={active ? "#fff" : "currentColor"} strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8" y2="16"></line><line x1="16" y1="16" x2="16" y2="16"></line></svg>,
  User: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
  Phone: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>,
  Store: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>,
};

const Badge = ({ children, type = 'default' }) => {
  const configs = {
    success: { bg: THEME.successBg, color: '#065F46', border: '#34D399' },
    danger: { bg: THEME.dangerBg, color: '#991B1B', border: '#F87171' },
    warning: { bg: THEME.warningBg, color: '#92400E', border: '#FBBF24' },
    primary: { bg: '#E0E7FF', color: THEME.primary, border: '#818CF8' },
    default: { bg: '#F1F5F9', color: THEME.textMuted, border: '#CBD5E1' }
  };
  const { bg, color, border } = configs[type] || configs.default;
  return <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, backgroundColor: bg, color, border: `1px solid ${border}`, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{children}</span>;
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, background: '#1E293B', color: '#fff', padding: '16px 24px', borderRadius: 16, zIndex: 9999, animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)', display: 'flex', alignItems: 'center', gap: 12, boxShadow: THEME.shadowLg, border: '1px solid #334155' }}>
      {type === 'success' ? <div style={{color:THEME.success, fontSize: 18}}>✔</div> : <div style={{color:THEME.danger, fontSize: 18}}>⚠</div>}
      <span style={{fontSize: 14, fontWeight: 600}}>{message}</span>
    </div>
  );
};

// ============================================================================
// 🚀 MAIN COMPONENT
// ============================================================================
export default function Mensagens() {
  injectStyles();

  const [waStatus, setWaStatus] = useState({ state: 'CHECKING', connected: false });
  const [qrCode, setQrCode] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [templatesRegua, setTemplatesRegua] = useState([]); // NOVO ESTADO
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); 
  const [historico, setHistorico] = useState({});
  const [typedMessage, setTypedMessage] = useState('');
  const [toast, setToast] = useState(null);
  
  const [search, setSearch] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const socket = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedIdRef = useRef(null);
  const clientesRef = useRef([]);

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { clientesRef.current = clientes; }, [clientes]);

  const getHeaders = useCallback(() => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('vizumToken')}` }), []);
  const showToast = (msg, type) => setToast({ msg, type });
  const scrollToBottom = () => setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  // SOCKET & FETCH
  const handleIncomingMessage = useCallback((data) => {
    const targetId = data.context?.osId || clientesRef.current.find(c => c.whatsappClean === data.from)?.id;
    if (!targetId) return;

    const newMessage = { text: data.body, sender: 'client', time: formatTime(data.rawDate), rawDate: data.rawDate, type: 'chat' };

    setHistorico(prev => {
      const chat = prev[targetId] || [];
      if (chat.some(m => m.text === newMessage.text && Math.abs(new Date(m.rawDate) - new Date(newMessage.rawDate)) < 2000)) return prev;
      return { ...prev, [targetId]: [...chat, newMessage] };
    });
    if (selectedIdRef.current === targetId) scrollToBottom();
  }, []);

  useEffect(() => {
    const initSocket = async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsapp/status`, { headers: getHeaders() });
        const d = await res.json();
        setWaStatus({ state: d.status, connected: d.connected });
      } catch { setWaStatus({ state: 'DISCONNECTED', connected: false }); }

      socket.current = io(API_URL, SOCKET_OPTS);
      socket.current.on('whatsapp_status', (d) => { setWaStatus({ state: d.status, connected: d.connected }); if (d.connected) setQrCode(null); });
      socket.current.on('qrCode', setQrCode);
      socket.current.on('incoming_message', handleIncomingMessage);
    };
    initSocket();
    return () => socket.current?.disconnect();
  }, [getHeaders, handleIncomingMessage]);

  const fetchClientes = useCallback(async () => {
    setIsSyncing(true);
    try {
      const res = await fetch(`${API_URL}/ordens-servico`, { headers: getHeaders() });
      const data = await res.json();
      
      const normalized = data.map(os => ({
        ...os, id: os._id, whatsappClean: os.whatsapp?.replace(/\D/g, '') || '',
        cliente: os.cliente || { nome: 'Consumidor' }, paciente: os.paciente || { nome: os.cliente?.nome },
        dataCompra: os.dataCompra || new Date().toISOString(), produto: os.produto || { lente: '-', armacao: '-' },
        receita: os.receita || { od: {}, oe: {} }, registros: os.registros || [],
        automacao: os.automacao || { ativo: true, mesesEnviados: [] }
      }));
      setClientes(normalized);
    } catch { showToast('Erro ao atualizar dados', 'error'); } 
    finally { setIsSyncing(false); }
  }, [getHeaders]);

  // NOVO: BUSCAR TEMPLATES DO BANCO
  const fetchTemplates = useCallback(async () => {
    try {
        const res = await fetch(`${API_URL}/api/templates`, { headers: getHeaders() });
        if(res.ok) {
            const data = await res.json();
            // Ordenação
            const sorted = data.sort((a, b) => {
                const order = ['0', '7d', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
                const idxA = order.indexOf(String(a.marco));
                const idxB = order.indexOf(String(b.marco));
                return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
            });
            setTemplatesRegua(sorted);
        }
    } catch (e) { console.error('Erro ao buscar templates', e); }
  }, [getHeaders]);

  useEffect(() => { 
      fetchClientes(); 
      fetchTemplates();
  }, [fetchClientes, fetchTemplates]);

  useEffect(() => {
    if (!selectedId) return;
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsapp/history/${selectedId}`, { headers: getHeaders() });
        const chatData = res.ok ? await res.json() : [];
        const client = clientes.find(c => c.id === selectedId);
        
        const timeline = (client?.registros || []).map(r => ({ text: r.texto, sender: r.texto.toLowerCase().includes('robô') ? 'bot' : 'system', time: formatTime(r.createdAt), rawDate: r.createdAt, type: 'event' }));
        const chat = chatData.map(m => ({ text: m.text, sender: m.sender, time: m.time, rawDate: m.rawDate, type: 'chat' }));

        setHistorico(prev => ({ ...prev, [selectedId]: [...chat, ...timeline].sort((a,b) => new Date(a.rawDate) - new Date(b.rawDate)) }));
        scrollToBottom();
      } catch (e) { console.error(e); }
    };
    loadHistory();
  }, [selectedId, clientes, getHeaders]);

  // ACTIONS
  const sendMessage = async (textOverride = null) => {
    const text = textOverride || typedMessage;
    if (!text.trim() || !selectedId) return;
    
    setHistorico(prev => ({ ...prev, [selectedId]: [...(prev[selectedId] || []), { text, sender: 'me', time: formatTime(new Date()), rawDate: new Date().toISOString(), type: 'chat' }] }));
    if(!textOverride) setTypedMessage('');
    scrollToBottom();

    try {
      const client = clientes.find(c => c.id === selectedId);
      await fetch(`${API_URL}/api/whatsapp/send`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ number: client.whatsappClean, message: text, osId: selectedId }) });
    } catch { showToast('Falha ao comunicar com o servidor', 'error'); }
  };

  const toggleRobot = async () => {
    if (!selectedId) return;
    const client = clientes.find(c => c.id === selectedId);
    const novoStatus = !client.automacao?.ativo;

    setClientes(prev => prev.map(c => c.id === selectedId ? { ...c, automacao: { ...c.automacao, ativo: novoStatus } } : c));
    showToast(novoStatus ? 'Automação Ativada' : 'Automação Pausada', novoStatus ? 'success' : 'warning');

    try {
      await fetch(`${API_URL}/ordens-servico/${selectedId}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify({ automacao: { ...client.automacao, ativo: novoStatus } }) });
    } catch { fetchClientes(); }
  };

  // RENDER HELPERS
  const filteredClients = useMemo(() => {
    return clientes.filter(c => {
      const term = search.toLowerCase();
      const matchSearch = c.cliente.nome.toLowerCase().includes(term) || String(c.numeroOS).includes(term) || String(c.vendedor).toLowerCase().includes(term);
      const matchMes = filtroMes === '' || new Date(c.dataCompra).getMonth() === parseInt(filtroMes);
      const matchStatus = filtroStatus === '' || c.status === filtroStatus;
      return matchSearch && matchMes && matchStatus;
    }).sort((a, b) => new Date(b.dataCompra) - new Date(a.dataCompra));
  }, [clientes, search, filtroMes, filtroStatus]);

  const selectedClient = clientes.find(c => c.id === selectedId);
  const groupedMessages = groupMessagesByDate(historico[selectedId] || []);

 const getDynamicTemplateStatus = (client, marco) => {
    const marcoStr = String(marco); // Padroniza o marco atual como texto (ex: '0', '7d', '1')

    // 1. Verifica no banco de dados se esse marco está dentro do array 'mesesEnviados'
    // Usamos o .some() e String() para garantir que Número 0 e String '0' sejam tratados como iguais.
    const jaEnviado = client.automacao?.mesesEnviados?.some(m => String(m) === marcoStr);
    
    if (jaEnviado) return 'SENT';
    
    // 2. REGRA DO STATUSMSG: Se a OS foi entregue/enviada e é o marco 0
    if (marcoStr === '0' && client.statusmsg === 'Enviada') return 'SENT';
    
    // 3. Cálculos de data (para saber se está Atrasado, Futuro ou Sugerido)
    const baseDate = client.automacao?.dataInicioRegua ? new Date(client.automacao.dataInicioRegua) : new Date(client.dataCompra);
    const diffDays = Math.ceil(Math.abs(new Date() - baseDate) / (1000 * 60 * 60 * 24));
    
    const targetDays = converterMarcoParaDias(marco);

    // Regra de tempo para sugerir o envio da mensagem
    if (Math.abs(diffDays - targetDays) <= 5 || (targetDays === 0 && diffDays < 3)) return 'SUGGESTED';
    
    return diffDays > targetDays ? 'LATE' : 'FUTURE';
  };

  // ============================================================================
  // 🎨 JSX RENDER
  // ============================================================================
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: THEME.bgBase, overflow: 'hidden' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <header className="glass-header" style={{ height: 74, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 32px', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 42, height: 42, background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`, color: '#fff', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, boxShadow: `0 4px 12px ${THEME.primaryGlow}` }}>V</div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: THEME.textMain, letterSpacing: '-0.5px' }}>Comms Hub</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: THEME.textMuted, marginTop: 2, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: waStatus.connected ? THEME.success : THEME.danger, boxShadow: waStatus.connected ? `0 0 10px ${THEME.success}` : 'none' }} />
              {waStatus.connected ? 'Server Online' : 'Gateway Offline'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={() => window.location.href='/home'} className="btn-epic" style={{ background: 'transparent', color: THEME.textMuted, border: `1px solid ${THEME.border}`, padding: '10px 20px', borderRadius: 10 }}>Sair</button>
          {!waStatus.connected && <button className="btn-epic" onClick={() => fetch(`${API_URL}/api/whatsapp/connect`, {method:'POST',headers:getHeaders()})} style={{ background: THEME.dangerBg, color: THEME.danger, padding: '10px 20px', borderRadius: 10 }}>🔌 Conectar</button>}
          <button onClick={() => {fetchClientes(); fetchTemplates();}} className="btn-epic" style={{ background: THEME.textMain, color: '#fff', padding: '10px 20px', borderRadius: 10, boxShadow: THEME.shadowSm }}>
            {isSyncing ? 'Sincronizando...' : 'Atualizar Dados'}
          </button>
        </div>
      </header>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        
        <aside style={{ width: 380, background: THEME.bgSurface, borderRight: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', zIndex: 5 }}>
          <div style={{ padding: 24, borderBottom: `1px solid ${THEME.border}`, background: THEME.bgBase }}>
            <div className="input-focus-ring" style={{ position: 'relative', marginBottom: 16, borderRadius: 12, border: `1px solid ${THEME.border}`, background: '#fff', transition: 'all 0.2s' }}>
              <div style={{ position: 'absolute', left: 16, top: 14 }}><Icons.Search /></div>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cliente, OS ou vendedor..." style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: 12, border: 'none', background: 'transparent', fontSize: 14, fontWeight: 500, outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <select value={filtroMes} onChange={e=>setFiltroMes(e.target.value)} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `1px solid ${THEME.border}`, background: '#fff', fontSize: 13, fontWeight: 600, color: THEME.textMuted, outline: 'none', cursor: 'pointer' }}>
                <option value="">Período</option>
                {MONTHS.map((m,i)=><option key={i} value={i}>{m}</option>)}
              </select>
              <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `1px solid ${THEME.border}`, background: '#fff', fontSize: 13, fontWeight: 600, color: THEME.textMuted, outline: 'none', cursor: 'pointer' }}>
                <option value="">Status OS</option>
                {OS_STATUSES.map(s=><option key={s} value={s}>{s.split(' ')[0]}</option>)}
              </select>
            </div>
          </div>
          
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fff' }}>
            {filteredClients.length === 0 ? (
               <div style={{ textAlign: 'center', padding: 40, color: THEME.textMuted, fontWeight: 500 }}>Nenhum resultado.</div>
            ) : (
              filteredClients.map(c => (
                <div key={c.id} onClick={() => setSelectedId(c.id)} className={`client-card ${selectedId === c.id ? 'active' : ''}`} style={{ padding: '16px 20px', margin: '0 0 10px 0', borderRadius: 14, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ fontSize: 15, fontWeight: 700, color: selectedId === c.id ? THEME.primary : THEME.textMain, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.cliente.nome}</strong>
                    <span style={{ fontSize: 12, fontWeight: 800, color: selectedId === c.id ? THEME.primary : THEME.textMuted, background: selectedId === c.id ? THEME.primaryGlow : THEME.bgBase, padding: '2px 8px', borderRadius: 6 }}>#{c.numeroOS}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: THEME.textMuted }}>{formatDate(c.dataCompra)}</span>
                    <Badge type={c.status === 'Entregue' ? 'success' : (c.status.includes('laboratório') ? 'warning' : 'primary')}>{c.status}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', background: THEME.bgSurface }}>
          {selectedClient ? (
            <>
              <div style={{ borderBottom: `1px solid ${THEME.border}`, padding: '24px 40px', background: '#fff', zIndex: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: THEME.primaryGlow, color: THEME.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800 }}>
                      {selectedClient.cliente.nome[0]}
                    </div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: THEME.textMain, letterSpacing: '-0.5px' }}>{selectedClient.cliente.nome}</h2>
                      <div style={{ display: 'flex', gap: 16, fontSize: 13, color: THEME.textMuted, marginTop: 6, fontWeight: 500 }}>
                        <span style={{display: 'flex', alignItems: 'center', gap: 4}}><Icons.Phone /> {selectedClient.whatsapp || 'N/A'}</span>
                        <span style={{display: 'flex', alignItems: 'center', gap: 4}}><Icons.User /> Vendedor: {selectedClient.vendedor}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mac-tabs">
                    {[
                      { id: 'chat', label: '💬 Chat Live' },
                      { id: 'details', label: '📝 Receita' },
                      { id: 'templates', label: `🤖 Motor CRM` },
                      { id: 'logs', label: '📜 Logs' }
                    ].map(tab => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`mac-tab ${activeTab === tab.id ? 'active' : ''}`}>
                        {tab.label} {tab.id === 'templates' && <span style={{display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: selectedClient.automacao?.ativo ? THEME.success : THEME.danger, marginLeft: 6}}/>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }} className={activeTab === 'chat' ? 'chat-bg' : ''}>
                
                {activeTab === 'chat' && (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '30px 15%' }}>
                      
                      {Object.keys(groupedMessages).map(date => (
                        <div key={date} style={{ animation: 'fadeIn 0.4s' }}>
                          <div style={{ textAlign: 'center', margin: '24px 0' }}>
                            <span style={{ background: THEME.glass, backdropFilter: 'blur(8px)', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, color: THEME.textMuted, boxShadow: THEME.shadowSm, border: `1px solid ${THEME.border}`, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              {date}
                            </span>
                          </div>
                          {groupedMessages[date].map((msg, i) => {
                            const isMe = msg.sender === 'me';
                            const isSys = msg.type === 'event' || msg.sender === 'system';
                            const isBot = msg.sender === 'bot';

                            if (isSys) return (
                              <div key={i} style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                                <div style={{ background: '#fff', color: THEME.textMuted, padding: '8px 20px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: `1px solid ${THEME.border}`, boxShadow: THEME.shadowSm }}>
                                  ⚙️ {msg.text}
                                </div>
                              </div>
                            );

                            return (
                              <div key={i} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 20 }}>
                                <div className={`bubble ${isMe ? 'bubble-me' : isBot ? 'bubble-bot' : 'bubble-other'}`}>
                                  {isBot && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#059669', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}><Icons.Robot active={true}/> Motor Vizum</div>}
                                  <div style={{ whiteSpace: 'pre-wrap', fontWeight: isMe ? 500 : 400 }}>{msg.text}</div>
                                  <div style={{ fontSize: 10, textAlign: 'right', marginTop: 8, opacity: isMe ? 0.8 : 0.5, fontWeight: 600 }}>{msg.time}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                    
                    <div style={{ padding: '0 15% 30px', position: 'absolute', bottom: 0, width: '100%', background: 'linear-gradient(to top, rgba(248, 250, 252, 1) 40%, rgba(248, 250, 252, 0))' }}>
                      <div className="glass-input input-focus-ring" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', padding: 10, borderRadius: 24, border: `1px solid ${THEME.border}`, boxShadow: THEME.shadowLg }}>
                        <textarea 
                          value={typedMessage} onChange={e=>setTypedMessage(e.target.value)} 
                          onKeyDown={e=>{if(e.key==='Enter' && !e.shiftKey){e.preventDefault(); sendMessage();}}} 
                          placeholder="Digite sua mensagem para o cliente..." 
                          style={{ flex: 1, padding: '14px 20px', borderRadius: 16, border: 'none', background: 'transparent', fontSize: 15, fontWeight: 500, resize: 'none', outline: 'none', minHeight: 52, maxHeight: 150, fontFamily: 'Inter' }} 
                          disabled={!waStatus.connected}
                        />
                        <button onClick={()=>sendMessage()} disabled={!typedMessage.trim() || !waStatus.connected} className="btn-epic" style={{ width: 52, height: 52, borderRadius: 16, background: typedMessage.trim() ? THEME.primary : THEME.bgChat, color: typedMessage.trim() ? '#fff' : THEME.textMuted, boxShadow: typedMessage.trim() ? `0 8px 16px ${THEME.primaryGlow}` : 'none' }}>
                          <Icons.Send />
                        </button>
                      </div>
                      {!waStatus.connected && <div style={{textAlign:'center', fontSize: 12, color: THEME.danger, marginTop: 8, fontWeight: 600}}>⚠️ WhatsApp desconectado. Conecte no topo da tela.</div>}
                    </div>
                  </div>
                )}

                {activeTab === 'details' && (
                  <div style={{ padding: '40px 10%', animation: 'popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                    <div style={{ background: '#fff', borderRadius: 24, border: `1px solid ${THEME.border}`, boxShadow: THEME.shadowLg, overflow: 'hidden', marginBottom: 32 }}>
                      <div style={{ background: THEME.textMain, padding: '24px 32px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                           <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1.5, color: '#94A3B8' }}>Ordem de Produção</h3>
                           <div style={{ fontSize: 28, fontWeight: 800 }}>#{selectedClient.numeroOS}</div>
                        </div>
                        <Icons.Store />
                      </div>
                      
                      <div style={{ padding: 32 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginBottom: 32 }}>
                           <div>
                              <span style={{fontSize: 12, fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase'}}>Lente Prescrita</span>
                              <div style={{fontSize: 18, fontWeight: 600, color: THEME.textMain, marginTop: 4}}>{selectedClient.produto.lente}</div>
                           </div>
                           <div>
                              <span style={{fontSize: 12, fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase'}}>Armação Escolhida</span>
                              <div style={{fontSize: 18, fontWeight: 600, color: THEME.textMain, marginTop: 4}}>{selectedClient.produto.armacao}</div>
                           </div>
                        </div>

                        <div style={{ border: `1px solid ${THEME.border}`, borderRadius: 16, overflow: 'hidden' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '80px repeat(5, 1fr)', background: THEME.bgChat, padding: '16px', fontSize: 12, fontWeight: 800, color: THEME.textMuted, textTransform: 'uppercase' }}>
                            <div>Olho</div><div>Esf.</div><div>Cil.</div><div>Eixo</div><div>DNP</div><div>Alt.</div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '80px repeat(5, 1fr)', padding: '20px 16px', borderBottom: `1px solid ${THEME.border}`, alignItems: 'center', background: '#fff' }}>
                            <div style={{fontWeight: 800, color: THEME.primary, fontSize: 16}}>O.D.</div>
                            <div style={{fontSize: 18, fontWeight: 700}}>{selectedClient.receita.od?.esf || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.od?.cil || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.od?.eixo || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.od?.dnp || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.od?.aco || '-'}</div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '80px repeat(5, 1fr)', padding: '20px 16px', alignItems: 'center', background: '#fff' }}>
                            <div style={{fontWeight: 800, color: THEME.secondary, fontSize: 16}}>O.E.</div>
                            <div style={{fontSize: 18, fontWeight: 700}}>{selectedClient.receita.oe?.esf || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.oe?.cil || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.oe?.eixo || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.oe?.dnp || '-'}</div>
                            <div style={{fontSize: 18, fontWeight: 500}}>{selectedClient.receita.oe?.aco || '-'}</div>
                          </div>
                          <div style={{ background: THEME.bgBase, padding: '16px 24px', borderTop: `1px dashed ${THEME.border}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                            <span style={{color: THEME.textMuted, fontSize: 13, fontWeight: 700, textTransform: 'uppercase'}}>Adição Perto:</span>
                            <strong style={{fontSize: 22, color: THEME.textMain, background: '#fff', padding: '4px 12px', borderRadius: 8, border: `1px solid ${THEME.border}`}}>{selectedClient.receita.adicao || '-'}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. CRM AUTOMATION: DINÂMICO DB */}
                {activeTab === 'templates' && (
                  <div style={{ padding: '40px 10%', animation: 'popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                    
                    <div style={{ background: '#fff', borderRadius: 24, padding: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40, border: `1px solid ${selectedClient.automacao?.ativo ? '#6EE7B7' : '#FCA5A5'}`, boxShadow: selectedClient.automacao?.ativo ? '0 10px 30px -10px rgba(16,185,129,0.2)' : THEME.shadowMd, position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 8, background: selectedClient.automacao?.ativo ? THEME.success : THEME.danger }} />
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingLeft: 16 }}>
                        <div className={selectedClient.automacao?.ativo ? 'pulseSoft' : ''} style={{ width: 64, height: 64, borderRadius: 20, background: selectedClient.automacao?.ativo ? THEME.success : THEME.dangerBg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}>
                          <Icons.Robot active={selectedClient.automacao?.ativo} />
                        </div>
                        <div>
                          <h2 style={{ margin: 0, color: THEME.textMain, fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px' }}>{selectedClient.automacao?.ativo ? 'Piloto Automático ON' : 'Automação Pausada'}</h2>
                          <p style={{ margin: '6px 0 0 0', color: THEME.textMuted, fontSize: 15, fontWeight: 500 }}>
                            A régua inicia a partir de: <strong style={{color: THEME.textMain}}>{selectedClient.automacao?.dataInicioRegua ? formatDate(selectedClient.automacao.dataInicioRegua) : 'Data da Compra'}</strong>
                          </p>
                        </div>
                      </div>
                      <button onClick={toggleRobot} className="btn-epic" style={{ padding: '14px 28px', borderRadius: 14, background: selectedClient.automacao?.ativo ? THEME.dangerBg : THEME.success, color: selectedClient.automacao?.ativo ? THEME.danger : '#fff', fontSize: 15 }}>
                        {selectedClient.automacao?.ativo ? 'Desativar Motor' : 'Ativar Motor CRM'}
                      </button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: THEME.textMain }}>Gatilhos Dinâmicos do Banco</h3>
                        <span style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 600 }}>{templatesRegua.length} Regras Ativas</span>
                    </div>

                    {templatesRegua.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, border: `1px dashed ${THEME.border}`, borderRadius: 20, background: '#fff' }}>
                            <p style={{ color: THEME.textMuted }}>Nenhum template configurado no banco de dados.</p>
                            <button onClick={() => window.location.href='/home'} className="btn-epic" style={{ background: THEME.primary, color: '#fff', padding: '10px 20px', borderRadius: 10, margin: '10px auto 0' }}>Ir para Editor de Msgs</button>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 }}>
                          {templatesRegua.map((t) => {
                            const status = getDynamicTemplateStatus(selectedClient, t.marco);
                            const isSent = status === 'SENT';
                            
                            // Processa o texto com a função auxiliar (Substitui {{nome}})
                            const body = formatarTexto(t.mensagem, selectedClient.cliente.nome);
                            
                            // Define um ícone visual baseado no marco (Apenas cosmético)
                            let icon = '📅';
                            if (t.marco === '0') icon = '🚀';
                            else if (t.marco === '7d') icon = '👓';
                            else if (t.marco === '12') icon = '🎂';

                            return (
                              <div key={t._id} style={{ background: '#fff', padding: 24, borderRadius: 20, border: `1px solid ${isSent ? '#D1FAE5' : THEME.border}`, position: 'relative', display: 'flex', flexDirection: 'column', boxShadow: THEME.shadowSm, transition: 'all 0.3s', opacity: isSent ? 0.8 : 1 }}>
                                {isSent && <div style={{ position: 'absolute', top: 20, right: 20 }}><Badge type="success">Executado</Badge></div>}
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                                  <div style={{ width: 48, height: 48, borderRadius: 16, background: isSent ? '#ECFDF5' : THEME.bgChat, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{icon}</div>
                                  <div>
                                    <strong style={{ display: 'block', fontSize: 15, color: THEME.textMain, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{t.titulo}</strong>
                                    <span style={{ fontSize: 12, color: THEME.primary, fontWeight: 600, background: THEME.bgBase, padding: '2px 8px', borderRadius: 4 }}>Marco: {t.marco === '7d' ? '7 dias' : t.marco === '0' ? 'Imediato' : `${t.marco} meses`}</span>
                                  </div>
                                </div>
                                
                                <div style={{ background: THEME.bgBase, padding: 16, borderRadius: 12, fontSize: 13, color: THEME.textMuted, lineHeight: 1.6, flex: 1, marginBottom: 24, whiteSpace: 'pre-wrap', border: `1px dashed ${THEME.border}` }}>
                                  {body}
                                </div>
                                
                                <button disabled={!waStatus.connected} onClick={() => sendMessage(body)} className="btn-epic" style={{ width: '100%', padding: '14px', borderRadius: 12, background: isSent ? '#fff' : THEME.textMain, color: isSent ? THEME.textMain : '#fff', border: isSent ? `1px solid ${THEME.border}` : 'none' }}>
                                  {isSent ? 'Disparar Novamente' : 'Forçar Disparo Agora'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                    )}
                  </div>
                )}

                {/* 4. LOGS SUPREME */}
                {activeTab === 'logs' && (
                  <div style={{ padding: '40px 15%', animation: 'fadeIn 0.3s' }}>
                     <h3 style={{ fontSize: 24, fontWeight: 800, color: THEME.textMain, marginBottom: 32 }}>Trilha de Auditoria</h3>
                      {selectedClient.registros?.length > 0 ? (
                        <div style={{ position: 'relative', paddingLeft: 30 }}>
                          <div style={{ position: 'absolute', left: 8, top: 20, bottom: 20, width: 2, background: THEME.border }} />
                          {selectedClient.registros.slice().reverse().map((reg, idx) => (
                            <div key={idx} style={{ position: 'relative', paddingBottom: 32 }}>
                              <div style={{ position: 'absolute', left: -28, top: 16, width: 14, height: 14, borderRadius: '50%', background: THEME.primary, border: '3px solid #fff', boxShadow: `0 0 0 2px ${THEME.primaryGlow}` }} />
                              <div style={{ background: '#fff', padding: 20, borderRadius: 16, border: `1px solid ${THEME.border}`, boxShadow: THEME.shadowSm }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                   <span style={{ fontSize: 12, fontWeight: 700, color: THEME.primary, textTransform: 'uppercase' }}>{reg.usuario ? 'Ação Manual' : 'Motor Automático'}</span>
                                   <span style={{ fontSize: 12, fontWeight: 600, color: THEME.textMuted }}>{new Date(reg.createdAt).toLocaleString('pt-BR')}</span>
                                </div>
                                <div style={{ fontSize: 15, color: THEME.textMain, fontWeight: 500, lineHeight: 1.5 }}>{reg.texto}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 24, border: `1px dashed ${THEME.border}` }}>
                           <span style={{fontSize: 40}}>📭</span>
                           <h4 style={{margin: '16px 0 8px 0', color: THEME.textMain}}>Nenhum evento registrado</h4>
                           <p style={{color: THEME.textMuted, fontSize: 14, margin: 0}}>As ações tomadas nesta OS aparecerão aqui.</p>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: THEME.bgChat }}>
              {qrCode ? (
                 <div style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', padding: 48, borderRadius: 32, boxShadow: THEME.shadowLg, textAlign: 'center', border: '1px solid rgba(255,255,255,1)', maxWidth: 460, animation: 'popIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                    <div style={{ width: 80, height: 80, background: 'linear-gradient(135deg, #25D366, #128C7E)', borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 10px 25px rgba(37, 211, 102, 0.3)' }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="#fff"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.099.824zm-3.423-14.416c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm.029 18.88c-1.161 0-2.305-.292-3.318-.844l-3.677.964.984-3.595c-.607-1.052-.927-2.246-.926-3.468.001-5.824 4.74-10.563 10.565-10.564 5.824 0 10.564 4.74 10.564 10.565 0 5.824-4.74 10.564-10.564 10.564z"/></svg>
                    </div>
                    <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 12px 0', color: THEME.textMain, letterSpacing: '-0.5px' }}>Conectar Gateway</h2>
                    <p style={{ color: THEME.textMuted, marginBottom: 32, fontSize: 15, lineHeight: 1.5, fontWeight: 500 }}>Abra o WhatsApp no aparelho da loja, vá em <strong>Aparelhos Conectados</strong> e aponte a câmera.</p>
                    <div style={{ padding: 16, background: '#fff', borderRadius: 24, border: `1px solid ${THEME.border}`, display: 'inline-block', boxShadow: THEME.shadowSm }}>
                      <img src={qrCode} alt="QR" style={{ width: 280, height: 280, display: 'block', borderRadius: 8 }} />
                    </div>
                 </div>
              ) : (
                 <div style={{ textAlign: 'center', animation: 'fadeIn 0.5s' }}>
                    <div style={{ width: 120, height: 120, background: `linear-gradient(135deg, ${THEME.primaryGlow}, #E0E7FF)`, borderRadius: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px', transform: 'rotate(-10deg)', boxShadow: THEME.shadowLg }}>
                      <span style={{ fontSize: 56 }}>✨</span>
                    </div>
                    <h2 style={{ fontSize: 32, fontWeight: 800, color: THEME.textMain, margin: '0 0 16px 0', letterSpacing: '-1px' }}>Seu Hub de Relacionamento</h2>
                    <p style={{ fontSize: 16, color: THEME.textMuted, maxWidth: 400, margin: '0 auto', lineHeight: 1.6, fontWeight: 500 }}>
                      Selecione um cliente no painel lateral para acessar o chat ao vivo, gerenciar automações e visualizar o ticket médico.
                    </p>
                 </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}